/**
 * Quy tắc mật khẩu dùng chung cho MỌI chỗ đặt mật khẩu: đăng ký, đổi/thêm mật khẩu
 * trong tài khoản, và đặt lại từ link trong email.
 *
 * 🔴 MỘT nguồn duy nhất — ba màn hình ấy ràng buộc nhau chặt hơn vẻ ngoài: đặt được
 * một mật khẩu mà màn đăng nhập không gửi lên được (xem `basicAuthHeader` trong
 * `boide-api.ts`) là ca hỏng tệ nhất — người dùng đổi xong thì mất luôn đường vào.
 */

/** Firebase tối thiểu 6; ta khắt khe hơn vì đây là tài khoản có ví tiền. */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 64;

/**
 * 🔴 CHỈ ASCII in được. Không phải chuyện thẩm mỹ: `/v1/users/login` dùng HTTP Basic,
 * mà Starlette giải bằng `.decode("ascii")` → mật khẩu có dấu tiếng Việt sẽ đăng nhập
 * KHÔNG được, dù BE nhận nó qua JSON lúc đăng ký hoàn toàn bình thường.
 */
const ASCII_PRINTABLE = /^[\x20-\x7E]+$/;
const HAS_LETTER = /[A-Za-z]/;
const HAS_DIGIT = /[0-9]/;

/** Trả về câu lỗi tiếng Việt, hoặc `null` khi mật khẩu hợp lệ. */
export function validatePassword(password: string): string | null {
  if (!password) return "Vui lòng nhập mật khẩu.";
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Mật khẩu phải có ít nhất ${PASSWORD_MIN_LENGTH} ký tự.`;
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    return `Mật khẩu tối đa ${PASSWORD_MAX_LENGTH} ký tự.`;
  }
  if (!ASCII_PRINTABLE.test(password)) {
    return "Mật khẩu chỉ dùng chữ không dấu, số và ký hiệu (không dùng tiếng Việt có dấu hay emoji).";
  }
  if (!HAS_LETTER.test(password) || !HAS_DIGIT.test(password)) {
    return "Mật khẩu cần có cả chữ và số.";
  }
  return null;
}

/** Kiểm cặp "mật khẩu mới" + "nhập lại" trong một lần — dùng chung ở 3 màn. */
export function validateNewPassword(password: string, confirm: string): string | null {
  const error = validatePassword(password);
  if (error) return error;
  if (password !== confirm) return "Hai lần nhập mật khẩu chưa khớp.";
  return null;
}

export type PasswordStrength = { score: 0 | 1 | 2 | 3; label: string; className: string };

/**
 * Thước đo ĐỘNG VIÊN, không phải hàng rào — hàng rào là `validatePassword`.
 * Cố ý thô sơ: một thư viện đo entropy cho màn này là thêm vài chục KB vào bundle
 * để nói cùng một câu.
 */
export function passwordStrength(password: string): PasswordStrength {
  if (!password) return { score: 0, label: "Chưa nhập", className: "bg-muted" };
  let points = 0;
  if (password.length >= PASSWORD_MIN_LENGTH) points += 1;
  if (password.length >= 12) points += 1;
  if (HAS_LETTER.test(password) && HAS_DIGIT.test(password)) points += 1;
  if (/[^A-Za-z0-9]/.test(password)) points += 1;
  if (points <= 1) return { score: 1, label: "Yếu", className: "bg-destructive" };
  if (points === 2) return { score: 2, label: "Khá", className: "bg-amber-500" };
  if (points === 3) return { score: 2, label: "Khá", className: "bg-amber-500" };
  return { score: 3, label: "Mạnh", className: "bg-emerald-500" };
}

// ============================================================
// "Tài khoản này đã có mật khẩu chưa?"
// ============================================================
//
// 🔴 BE KHÔNG TRẢ LỜI ĐƯỢC CÂU NÀY. `login_type` chỉ ghi cách tài khoản được TẠO RA
// ('basic' | 'google') và không bao giờ đổi; một người đăng nhập Google rồi tự thêm
// mật khẩu vẫn mang `login_type='google'` mãi mãi, vì mật khẩu ấy nằm bên Firebase
// chứ không phải ở `users.hashed_password`.
//
// Hệ quả nếu chỉ tin `login_type`: lần thứ hai vào đổi mật khẩu, người đó lại được
// mời "Thêm mật khẩu" và KHÔNG bị hỏi mật khẩu hiện tại — đúng hàng rào vừa dựng lên
// lại hở. Cờ dưới đây vá phần thực dụng ngay tại máy đã từng đặt mật khẩu.
//
// ⚠️ Là GỢI Ý, không phải cơ chế bảo mật: xoá dữ liệu trình duyệt hoặc sang máy khác
// là mất. Muốn chắc chắn thì BE phải trả về việc tài khoản có provider 'password' hay
// không (Admin SDK đọc được `provider_data`) và tự kiểm mật khẩu cũ trong
// `/v1/users/reset-password` — xem ghi chú ở `changePassword`.

const HAS_PASSWORD_KEY_PREFIX = "boide_has_password_";

export function markHasPassword(username?: string | null) {
  if (typeof window === "undefined" || !username) return;
  try {
    localStorage.setItem(HAS_PASSWORD_KEY_PREFIX + username, "1");
  } catch {
    // Chế độ riêng tư chặn localStorage — mất gợi ý, không mất tính năng.
  }
}

export function knownHasPassword(username?: string | null): boolean {
  if (typeof window === "undefined" || !username) return false;
  try {
    return localStorage.getItem(HAS_PASSWORD_KEY_PREFIX + username) === "1";
  } catch {
    return false;
  }
}
